import { AfterViewInit, Component, ElementRef, Input, Optional, Self, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { IonModal, ModalController } from '@ionic/angular';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'c-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    standalone: false
})
export class SelectComponent<T = any> implements ControlValueAccessor, AfterViewInit {
  @Input() label: string | undefined;
  @Input() hidden: boolean | undefined;
  @Input() readonly: boolean | undefined;
  @Input() showError = this.control?.touched; // Default to touched state of the control for backward compatibility for components that don't set showError after clicking submit
  @Input() noBorder: boolean;
  @Input() enableSearch = false;
  @Input() loading = false;
  @Input() icon = faChevronDown;
  @Input() id: string;
  @Input() noResultsLabel: string = 'select.no-results';

  @ViewChild('prefixContent') prefixContent: ElementRef;
  showPrefixContent = false;

  private _options: any[] = [];
  @Input() set options(options: any[]) {
    this._options = options;
    if (!options?.length) {
      return;
    }
    if (this.control?.value) {
      this.displayedValue$.next(this.options?.find((option) => option.value === this.control.value)?.label);
    }
    this.handleAutoSelect();
  }
  get options() {
    return this._options;
  }
  private _autoSelect: AutoSelectType = false;
  @Input() set autoSelect(autoSelect: AutoSelectType) {
    this._autoSelect = autoSelect;
    this.handleAutoSelect();
  }
  get autoSelect() {
    return this._autoSelect;
  }

  displayedValue$ = new BehaviorSubject('');

  get control() {
    return this.ngControl?.control as FormControl;
  }

  @ViewChild(IonModal) modal: IonModal;

  constructor(@Optional() @Self() public ngControl: NgControl, private modalController: ModalController) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngAfterViewInit(): void {
    this.showPrefixContent = this.prefixContent?.nativeElement.childNodes?.length.value > 0;
  }

  private handleAutoSelect(): void {
    // This timeout is needed because otherwise not all properties are initialized.
    setTimeout(() => {
      switch (this.autoSelect) {
        case 'selectFirst':
          this.control?.setValue(this.options?.[0]?.value);
          break;
        case 'selectSingle':
          if (this.options?.length === 1) {
            this.control?.setValue(this.options?.[0]?.value);
          }
          break;
      }
    });
  }

  writeValue(data: any): void {
    this.displayedValue$.next(this.options?.find((option) => option.value === data)?.label);
  }

  onChange = (value: T) => {};

  onTouched = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onSelect(value: string): void {
    this.control?.setValue(value);
    this.control?.markAsDirty();
    this.closeModal();
  }

  closeModal(): void {
    this.control.markAllAsTouched();
    this.modal.dismiss();
  }
}

type AutoSelectType = 'selectFirst' | 'selectSingle' | false;
